import { Patient } from "../domain/patient";
import { WebStorageKeys } from "./WebStorageKeys";

/**
 * localStorageが使用可能であればlocalStorageを、使用不可であればsessionStorageを使用します
 */
export class WebStorageAccessor {
  private static get isLocalStorageEnable(): boolean {
    const defaultValue = true;
    const prefetchedData = (window as any).prefetchedData;
    if (!prefetchedData) { return defaultValue; }
    const feature = prefetchedData.feature;
    if (!feature) { return defaultValue; }
    const localStorageFeature = feature.localStorage;
    if (!localStorageFeature) { return defaultValue; }
    return localStorageFeature.isEnable;
  }

  private static get storage(): Storage {
    return this.isLocalStorageEnable
    ? localStorage
    : sessionStorage;
  }

  /**
   * sessionStorageを使う設定の時、Chromeではイベントが発生しない？模様
   */
  static addUpdateEventListener(key: string, listener: (e: StorageEvent) => void): void {
    window.addEventListener("storage", (e: StorageEvent) => {
      if (e.storageArea !== this.storage) {
        return;
      }
      if (e.key !== key) {
        return;
      }
      listener(e);
    });
  }

  static get hostName(): string {
    return this.storage.getItem(WebStorageKeys.hostName);
  }

  static set hostName(value: string) {
    if (!value) {
      this.storage.removeItem(WebStorageKeys.hostName);
      return;
    }
    this.storage.setItem(WebStorageKeys.hostName, value);
  }

  static get windowRectangle(): any {
    return JSON.parse(this.storage.getItem(WebStorageKeys.windowRectangle));
  }

  static set windowRectangle(value: any) {
    if (!value) {
      this.storage.removeItem(WebStorageKeys.windowRectangle);
      return;
    }
    const json = JSON.stringify(value);
    this.storage.setItem(WebStorageKeys.windowRectangle, json);
  }

  static get isDeveloperMode(): boolean {
    const value = this.storage.getItem(WebStorageKeys.isDeveloperMode);
    return value
        && (value.toLowerCase() === true.toString().toLowerCase());
  }

  static set isDeveloperMode(value: boolean) {
    this.storage.setItem(WebStorageKeys.isDeveloperMode, (!!value).toString());
  }

  static get searchedPatients(): Patient[] {
    const value = this.storage.getItem(WebStorageKeys.patientSearchHistory);
    return this.toPatients(value);
  }

  static set searchedPatients(value: Patient[]) {
    if (!value) {
      this.storage.removeItem(WebStorageKeys.patientSearchHistory);
      return;
    }
    const json = JSON.stringify(value);
    this.storage.setItem(WebStorageKeys.patientSearchHistory, json);
  }

  private static toPatients(value: string): Patient[] {
    if (!value) { return []; }
    const infos = JSON.parse(value);
    if (!infos || !Array.isArray(infos)) { return []; }
    return infos
      .map(i => this.toPatient(i))
      .filter(p => !!p);
  }

  private static toPatient(value: any): Patient {
    if (!value || !value.id) { return null; }
    return new Patient(
      value.id,
      value.name,
      value.kanaName,
      value.birthday,
      value.sex,
    );
  }
}
